import type { ExerciseTemplate } from '@yuan-math/exercises';
import { add, fraction, type Rng, roundTo, toLatex, toText } from '@yuan-math/math';
import { fractionExercise, fractionTrap, numericExercise, template, trap } from './authoring.ts';

/** A non-zero signed integer, so sign traps can never coincide with the answer. */
const signed = (rng: Rng, min: number, max: number): number => rng.int(min, max) * (rng.bool() ? 1 : -1);

/** Renders `a + (-3)` and `a - (-3)` the way a textbook does. */
const bracket = (value: number): string => (value < 0 ? `(${value})` : String(value));

/** Signed arithmetic across fractions, decimals and integers alike. */
export const RATIONAL_NUMBER_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'rational-add-signed',
    conceptId: 'rational-numbers',
    description: 'Add or subtract two signed integers.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 2 ? 12 : difficulty === 3 ? 30 : 80;
      const a = signed(rng, 2, limit);
      const b = signed(rng, 2, limit);
      const subtracting = rng.bool();
      const answer = subtracting ? a - b : a + b;

      const traps = [
        // Subtracting a negative read as an ordinary subtraction.
        ...(subtracting && b < 0 ? [trap(a + b, 'subtracting-negative')] : []),
        // Opposite signs, and the smaller magnitude taken as the winner.
        ...(answer !== 0 && a * b < 0 ? [trap(-answer, 'negative-magnitude-order')] : []),
      ];

      return numericExercise({
        prompt: 'Work this out.',
        latex: `${a} ${subtracting ? '-' : '+'} ${bracket(b)}`,
        value: answer,
        summary: subtracting
          ? `Subtracting ${b} is the same as adding ${-b}, which gives ${answer}.`
          : `Starting at ${a} and moving ${b < 0 ? 'left' : 'right'} by ${Math.abs(b)} lands on ${answer}.`,
        hint: 'Put the first number on the number line, then decide which way the second one moves you.',
        steps: [
          { label: 'Rewrite it as an addition.', latex: `${a} + ${bracket(subtracting ? -b : b)}` },
          { label: 'Move along the number line.', latex: `= ${answer}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'rational-multiply-signed',
    conceptId: 'rational-numbers',
    description: 'Multiply or divide two signed integers, and get the sign right.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const x = signed(rng, 2, difficulty === 2 ? 9 : 12);
      const y = signed(rng, 2, difficulty === 2 ? 9 : 12);
      const dividing = difficulty > 2 && rng.bool();
      const answer = dividing ? x : x * y;
      const negatives = [x, y].filter((value) => value < 0).length;

      return numericExercise({
        prompt: 'Work this out.',
        latex: dividing ? `${bracket(x * y)} \\div ${bracket(y)}` : `${bracket(x)} \\times ${bracket(y)}`,
        value: answer,
        summary:
          negatives === 1
            ? `One negative sign, so the answer is negative: ${answer}.`
            : `${negatives === 0 ? 'No' : 'Two'} negative signs, so the answer is positive: ${answer}.`,
        hint: 'Work out the digits first, then count how many negative signs there are.',
        steps: [
          { label: 'Ignore the signs and calculate.', latex: `${Math.abs(answer)}` },
          {
            label: `There ${negatives === 1 ? 'is one negative sign' : `are ${negatives} negative signs`}, so the answer is ${answer < 0 ? 'negative' : 'positive'}.`,
            latex: `${answer}`,
          },
        ],
        traps: [trap(-answer, 'negative-product-sign')],
      });
    },
  }),

  template({
    id: 'rational-signed-decimals',
    conceptId: 'rational-numbers',
    description: 'Add or subtract signed decimals, where the double negative is easy to lose.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const scale = difficulty === 3 ? 99 : 999;
      const a = roundTo(signed(rng, 11, scale) / 10, 1);
      const b = roundTo(signed(rng, 11, scale) / 10, 1);
      const subtracting = rng.bool();
      const answer = roundTo(subtracting ? a - b : a + b, 2);

      return numericExercise({
        prompt: 'Work this out.',
        latex: `${a} ${subtracting ? '-' : '+'} ${bracket(b)}`,
        value: answer,
        summary: subtracting
          ? `Subtracting ${b} is the same as adding ${roundTo(-b, 1)}, which gives ${answer}.`
          : `${a} and ${b} combine to ${answer}.`,
        hint: 'The decimal point changes nothing about the signs. Decide the direction first, then line up the places.',
        steps: [
          { label: 'Rewrite it as an addition.', latex: `${a} + ${bracket(roundTo(subtracting ? -b : b, 1))}` },
          { label: 'Line up the decimal points and combine.', latex: `= ${answer}` },
        ],
        traps: [
          ...(subtracting && b < 0 ? [trap(roundTo(a + b, 2), 'subtracting-negative')] : []),
          ...(answer !== 0 && a * b < 0 ? [trap(roundTo(-answer, 2), 'negative-magnitude-order')] : []),
        ],
      });
    },
  }),

  template({
    id: 'rational-fraction-to-decimal',
    conceptId: 'rational-numbers',
    description: 'Write a fraction as a decimal, keeping the sign.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const d = rng.pick(difficulty === 2 ? [2, 4, 5, 10] : [8, 16, 20, 25]);
      const n = rng.int(1, d - 1);
      const negative = rng.bool();
      const value = fraction(negative ? -n : n, d);
      const decimal = roundTo(value.n / value.d, 5);

      return numericExercise({
        prompt: 'Write this fraction as a decimal.',
        latex: toLatex(value),
        value: decimal,
        summary: `A fraction is a division: ${value.n} ÷ ${value.d} = ${decimal}.`,
        hint: 'The fraction bar means "divide". The sign comes along unchanged.',
        steps: [
          { label: 'Read the fraction as a division.', latex: `${value.n} \\div ${value.d}` },
          { label: 'Divide.', latex: `${decimal}` },
        ],
        traps: [trap(roundTo(value.d / value.n, 5), 'divide-wrong-direction')],
      });
    },
  }),

  template({
    id: 'rational-fraction-sum-exact',
    conceptId: 'rational-numbers',
    description: 'Add a positive and a negative fraction, giving an exact answer.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const d1 = rng.pick([2, 3, 4, 5, 6]);
      const d2 = rng.pick([2, 3, 4, 5, 6].filter((d) => d !== d1));
      const n1 = rng.int(1, d1 * 2 - 1);
      const n2 = rng.int(1, d2 * 2 - 1);
      const positive = fraction(n1, d1);
      const negative = fraction(-n2, d2);
      const answer = add(positive, negative);
      const ignoringSign = add(positive, fraction(n2, d2));

      return fractionExercise({
        prompt: 'Add these, and give your answer as a fraction in its simplest form.',
        latex: `${toLatex(positive)} + \\left(${toLatex(negative)}\\right)`,
        value: answer,
        requireSimplified: true,
        summary: `Adding a negative takes away, so this is ${toText(positive)} − ${toText(fraction(n2, d2))} = ${toText(answer)}.`,
        hint: 'Adding a negative is the same as subtracting. Find a common denominator, then subtract.',
        steps: [
          { label: 'Adding a negative is subtracting.', latex: `${toLatex(positive)} - ${toLatex(fraction(n2, d2))}` },
          { label: 'Rewrite over a common denominator and subtract.', latex: toLatex(answer) },
        ],
        traps: [fractionTrap(ignoringSign, 'negative-magnitude-order')],
      });
    },
  }),
];
